import { useEffect, useCallback } from 'react';
import { useSwipeGesture } from '@/hooks/useSwipeGesture';
import type { RadioPhoto } from '@/mocks/radioContent';

interface PhotoLightboxProps {
  photos: RadioPhoto[];
  currentIndex: number;
  isFavorite: boolean;
  onClose: () => void;
  onPrev: () => void;
  onNext: () => void;
  onToggleFavorite: () => void;
  onShare: () => void;
}

export default function PhotoLightbox({
  photos,
  currentIndex,
  isFavorite,
  onClose,
  onPrev,
  onNext,
  onToggleFavorite,
  onShare,
}: PhotoLightboxProps) {
  const photo = photos[currentIndex];
  const total = photos.length;

  const swipe = useSwipeGesture({
    threshold: 60,
    onSwipeLeft: onNext,
    onSwipeRight: onPrev,
  });

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') onPrev();
      if (e.key === 'ArrowRight') onNext();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose, onPrev, onNext]);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  const handleBackdropClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget) onClose();
    },
    [onClose],
  );

  if (!photo) return null;

  return (
    <div
      className="fixed inset-0 z-[80] bg-black/95 flex items-center justify-center"
      onClick={handleBackdropClick}
    >
      {/* Close button */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 z-20 w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center cursor-pointer hover:bg-white/20 transition-colors"
        aria-label="Cerrar"
      >
        <i className="ri-close-line text-white text-xl"></i>
      </button>

      {/* Counter */}
      <div className="absolute top-4 left-4 z-20 px-3 py-1.5 rounded-full bg-white/10 backdrop-blur-sm">
        <span className="text-white text-xs font-semibold">
          {currentIndex + 1} / {total}
        </span>
      </div>

      {/* Prev button */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          onPrev();
        }}
        className="absolute left-3 md:left-6 z-20 w-10 h-10 md:w-12 md:h-12 rounded-full bg-white/10 flex items-center justify-center cursor-pointer hover:bg-white/25 transition-colors"
        aria-label="Anterior"
      >
        <i className="ri-arrow-left-s-line text-white text-xl md:text-2xl"></i>
      </button>

      {/* Next button */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          onNext();
        }}
        className="absolute right-3 md:right-6 z-20 w-10 h-10 md:w-12 md:h-12 rounded-full bg-white/10 flex items-center justify-center cursor-pointer hover:bg-white/25 transition-colors"
        aria-label="Siguiente"
      >
        <i className="ri-arrow-right-s-line text-white text-xl md:text-2xl"></i>
      </button>

      {/* Image container */}
      <div
        className="max-w-5xl max-h-[80vh] w-full mx-4 flex items-center justify-center select-none"
        onTouchStart={swipe.handleTouchStart}
        onTouchMove={swipe.handleTouchMove}
        onTouchEnd={swipe.handleTouchEnd}
      >
        <img
          src={photo.url}
          alt={photo.caption}
          className="max-w-full max-h-[80vh] object-contain rounded-xl"
          draggable={false}
        />
      </div>

      {/* Bottom info bar */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 bg-white/10 backdrop-blur-xl rounded-2xl px-5 py-3 flex flex-col sm:flex-row items-center gap-3 sm:gap-5">
        <div className="text-center sm:text-left">
          <p className="text-white text-sm font-semibold leading-snug">{photo.caption}</p>
          <p className="text-white/50 text-[11px] mt-0.5">
            {photo.date} &bull; {photo.likes} me gusta
          </p>
        </div>

        <div className="flex items-center gap-2">
          {/* Save favorite */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              onToggleFavorite();
            }}
            className="w-9 h-9 rounded-xl flex items-center justify-center cursor-pointer transition-colors bg-white/10 hover:bg-white/20"
            aria-label={isFavorite ? 'Quitar de favoritas' : 'Guardar favorita'}
          >
            <i
              className={`text-lg ${isFavorite ? 'ri-heart-fill text-red-400' : 'ri-heart-line text-white'}`}
            ></i>
          </button>

          {/* Share */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              onShare();
            }}
            className="w-9 h-9 rounded-xl flex items-center justify-center cursor-pointer transition-colors bg-white/10 hover:bg-white/20"
            aria-label="Compartir"
          >
            <i className="ri-share-forward-line text-white text-lg"></i>
          </button>

          {/* View album */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-white/15 hover:bg-white/25 cursor-pointer transition-colors whitespace-nowrap"
          >
            <i className="ri-folder-open-line text-white text-sm"></i>
            <span className="text-white text-xs font-semibold">Ver álbum</span>
          </button>
        </div>
      </div>
    </div>
  );
}